/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
// import LeadershipForm from "pages/Entities/pages/Form/LeadershipForm";

import React, { useMemo } from "react";

import { Route, Routes as Switch } from "react-router-dom";

import { ROUTES, ROUTES_LIST, getRouteMap } from "Router";

import useProgress from "hooks/useProgress";
import { useAuth, useRole } from "state";

import { validateAdminRoute } from "pages/Admins/constants";
import AdminForm from "pages/Admins/pages/Form";
import ForgetPasswordPage from "pages/ForgetPassword/ForgetPassword";
import LoginPage from "pages/Login/Login";

import {
  ErrorBoundaryPage,
  HomePage,
  ServerError,
  UnAuthorized,
} from "../pages";

import {
  DynamicRoutesManager,
  FallbackManager,
  RouteManager,
} from "./RouterManagers";
import RoutesMapper from "./RoutesMapper";

const RoutesWrapper = () => {
  const { pages, getPermits } = useRole();
  const { user, isLoggedIn, isLoading: isAuthLoading } = useAuth();
  const [Progressbar] = useProgress();

  const routes = useMemo(
    () =>
      pages.map((page) => {
        const routeProps = RoutesMapper(page);
        return <Route key={page} {...routeProps} />;
      }),
    [pages],
  );

  return (
    <React.Suspense
      fallback={
        <div className="fixed top-0 z-20 inset-x-0" test-id="tF5g90jN">
          <Progressbar size="sm" test-id="3PSfn016" />
        </div>
      }
      test-id="KsY4Uj9G"
    >
      <Switch test-id="NGj5xPIB">
        <Route
          path={ROUTES.HOME}
          element={
            isLoggedIn || isAuthLoading ? (
              <RouteManager test-id="sS-IDrHi">
                <HomePage test-id="khYVCg8A" />
              </RouteManager>
            ) : (
              <LoginPage />
            )
          }
          test-id="qkupuWUp"
        />

        <Route path={ROUTES.FORGET_PASSWORD} element={<ForgetPasswordPage />} />
        <Route path={ROUTES.LOGIN} element={<LoginPage />} test-id="B8hZ9ihu" />

        {/* Dynamically create routes. */}
        {routes}
        {/* Allowed profiles */}

        {user?.role === "admin" && (
          <Route
            path={`${ROUTES.ADMINS}/:id`}
            element={
              <RouteManager test-id="3T_zt8bZ">
                <DynamicRoutesManager
                  fetcher={validateAdminRoute}
                  test-id="tIswROMp"
                >
                  <AdminForm
                    routeMap={getRouteMap(ROUTES.ADMINS)}
                    permissions={getPermits(ROUTES.ADMINS)}
                    pageHeaderProps={{}}
                    test-id="2ULSHEqA"
                  />
                </DynamicRoutesManager>
              </RouteManager>
            }
          />
        )}

        <Route
          path={ROUTES_LIST.unauthorized?.route}
          element={<UnAuthorized test-id="iHD_u81e" />}
          test-id="6-sW_Wk8"
        />
        <Route
          path={ROUTES_LIST.sorry?.route}
          element={<ServerError test-id="0V6HsHFF" />}
          test-id="R1ZU3TwY"
        />
        <Route
          path={ROUTES_LIST["error-boundary"]?.route}
          element={<ErrorBoundaryPage test-id="jeMzegO5" />}
          test-id="lagc--ds"
        />

        <Route
          path="*"
          element={<FallbackManager test-id="CyF1l6Mv" />}
          test-id="w14yH7Jc"
        />
      </Switch>
    </React.Suspense>
  );
};

export default RoutesWrapper;
